import {Component, ElementRef, EventEmitter, Inject, Input, OnInit, Output, ViewChild} from '@angular/core';
import {DialogService} from 'primeng/dynamicdialog';
import {
    ImpactLevel,
    ProjectConfiguration,
    RemedyForceConfiguration,
    UrgencyLevel
} from '../../../project/models/project-configuration';
import {MonitoringAlarm} from '../../../monitoring/models/monitoring-alarm';
import {Incident} from '../../models/incident';
import {IncidentDataService} from '../../services/incident-data.service';
import {MessageService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {MonitoredTrain} from '../../../monitoring/models/monitored-train';
import {FormValidationService} from "../../../../shared/services/form-validation.service";

@Component ( {
    selector: 'app-raise-incident-form',
    template: require('./raise-incident-form.component.html'),
    styles: [require('./raise-incident-form.component.sass')]
} )
export class RaiseIncidentFormComponent implements OnInit {

    @ViewChild ( 'raiseIncidentModal' ) raiseIncidentModal: ElementRef;
    @Input () monitoredTrains: MonitoredTrain[];
    @Output () submitIncident = new EventEmitter<{
        parentType: string,
        incident: Incident
    }> ();


    remedyForceConfiguration: RemedyForceConfiguration;
    eventIds: number[];
    parentIssue: MonitoringAlarm;
    trainFleet: string | undefined;
    description: string;
    vehicle: string;
    fleet: string;
    asset: string;
    trainName: string | undefined;
    alarm: MonitoringAlarm | undefined;
    incidentForm: FormGroup;
    raiseIncidentModalReference: NgbModalRef;


    constructor(
        private fb: FormBuilder,
        private modalService: NgbModal,
        private incidentDataService: IncidentDataService,
        private messageService: MessageService,
        private translate: TranslateService,
        public formValidation: FormValidationService,
    @Inject ( 'TimeZoneFactory' ) private timeZoneFactory: any,
        @Inject ( 'ConfigurationStoreService' ) private configurationStoreService: any,
    ) {
    }

    ngOnInit(): void {
        // TODO show current date by default when adding a new train
        this.incidentForm = this.fb.group ( {
            fleet: ['', Validators.required],
            vehicle: ['', Validators.required],
            asset: ['', Validators.required],
            description: ['', Validators.required],
            impactOnService: ['', Validators.required],
            proposedResolution: ['', Validators.required],
            assetClass: ['', Validators.required],
            urgency: ['', Validators.required],
            impact: ['', Validators.required],
            category: ['', Validators.required]
        } );
    }


    // set fleet and asset
    setFleetVehicleAsset = async (currentTrain: MonitoredTrain | undefined, alarms: MonitoringAlarm[] = []) => {
        let incidentData;
        this.trainFleet = currentTrain && currentTrain.trainType;
        this.trainName = currentTrain && currentTrain.name;
        this.parentIssue = alarms && alarms[0];
        this.eventIds = alarms.map ( alarm => alarm.eventId );
        this.description = alarms.reduce ( (acc, alarm) => acc ? `${acc} \n ${alarm.description}` : `${alarm.description}`, '' );

        const projectConfiguration = this.configurationStoreService.getProjectConfiguration ( this.parentIssue.projectId ) as ProjectConfiguration;
        this.remedyForceConfiguration = projectConfiguration && projectConfiguration.remedyForceConfiguration;
        // issue.fleet value format is <<projectName>>-<<trainType>>-<<onboardCmp>>
        // examples : 'dsb-ic4-mar3', 'sncf-duplex-mar3'
        const fixedFleets = this.remedyForceConfiguration && this.remedyForceConfiguration.fixedFleets;
        if (this.trainFleet) {
            if (fixedFleets && fixedFleets.length > 0) {
                const rfNameForFleet = fixedFleets.find ( (fixedFleet) => {
                    return fixedFleet.type === this.trainFleet;
                } );
                if (rfNameForFleet) {
                    this.fleet = rfNameForFleet.fleet;
                    // tslint:disable-next-line:max-line-length
                    try {
                        // tslint:disable-next-line:max-line-length
                        const data = await this.incidentDataService.getRfAttributes(this.parentIssue.projectId, this.trainName ? this.trainName : '',  rfNameForFleet.fleet);
                        if (data) {
                            this.asset = data;
                            this.vehicle = data;
                        }
                        else{
                            this.asset = '';
                            this.vehicle = '';
                        }
                    } catch (error) {
                        this.messageService.add ( {
                            severity: 'error',
                            summary: this.translate.instant ( 'I18N_RAISEINCIDENT.RAISE_INCIDENT' ),
                            detail: this.translate.instant ( 'I18N_RAISEINCIDENT.FAILED_TO_FETCH_INCIDENT_ATTRIBUTES' )
                        } );
                    }

                }

            } else {

                const fleet = this.trainFleet.toUpperCase ().match ( '^[^-\\s]+-[^-\\s]+' );  // DSB-IC4-MAR3, SNCF-DUPLEX-MAR3

                if (fleet) {

                    // matches to <<projectName>>-<<trainType>>, examples : 'DSB-IC4', 'SNCF-DUPLEX'
                    const fleetArray = fleet[0].split ( '-' );
                    const projectName = fleetArray[0];
                    const trainType = fleetArray[1];
                    // DSB - IC4
                    this.fleet = projectName + ' - ' + trainType;

                    // 5645 DSB
                    // tslint:disable-next-line:max-line-length
                    try {
                        const data =  await this.incidentDataService.getRfAttributes(this.parentIssue.projectId, this.trainName ? this.trainName : '',  projectName);
                        if (data) {
                            this.asset = data;
                            this.vehicle = data;
                        }
                    } catch (error) {
                        this.messageService.add ( {
                            severity: 'error',
                            summary: this.translate.instant ( 'I18N_RAISEINCIDENT.RAISE_INCIDENT' ),
                            detail: this.translate.instant ( 'I18N_RAISEINCIDENT.FAILED_TO_FETCH_INCIDENT_ATTRIBUTES' )
                        } );
                    }

                }
            }
        }
        incidentData = {
            asset: this.asset,
            fleet: this.fleet,
            vehicle: this.vehicle,
            description: this.description
        };
        return incidentData;
    }

    async showModal(currentTrain: MonitoredTrain | undefined, alarms?: MonitoringAlarm[]) {
        try {
            this.incidentForm.reset ();
            const incidentData = await this.setFleetVehicleAsset ( currentTrain, alarms );
            this.incidentForm.patchValue ( incidentData );
            this.raiseIncidentModalReference = await this.modalService.open ( this.raiseIncidentModal, {
                size: 'xl',
                backdrop: 'static'
            } );
        } catch (error) {
            this.messageService.add ( {
                severity: 'error',
                summary: this.translate.instant ( 'I18N_RAISEINCIDENT.RAISE_INCIDENT' ),
                detail: this.translate.instant ( 'I18N_RAISEINCIDENT.FAILED_TO_OPEN_INCIDENT' )
            } );
        }
    }

    onSubmit = async () => {
        if(this.incidentForm.valid){
            const eventTimestamp = this.timeZoneFactory.formatUtcToTz ( this.parentIssue.lastChange, null );
            const incident: Incident = {
                ...this.incidentForm.value,
                projectId: this.parentIssue && this.parentIssue.projectId,
                trainId: this.parentIssue && this.parentIssue.trainId,
                hostname: this.parentIssue && this.parentIssue.hostname,
                eventTimestamp,
                eventId: this.parentIssue && this.parentIssue.eventId
            };
            this.submitIncident.emit ( {
                parentType: this.parentIssue.type,
                incident: {...incident}
            } );
            this.raiseIncidentModalReference.close ();
        }
        else{
            this.formValidation.validateAllFormFields(this.incidentForm);
        }


    }

    cancel = () => {
        this.raiseIncidentModalReference.close ();
    }
}
